import { useQuery } from '@apollo/client'

import { FETCH_USER_GROCERY_LIST } from '../app/graphql/queries'
import { setGroceryListId, setGroceryListItems, setGroceryListItemsAndIngredientsList } from '../app/groceryList/groceryList.slice'
import { setIngredientsList } from '../app/homepage/homepage.slice'
import { userSelector } from '../app/main/mainSlice'
import { useAppDispatch, useAppSelector } from '../app/store/hooks'
import { getDatesFromNow } from '../components/mealPlanPage/utils'
import { useCallback } from 'react'
import { getDietArray } from '../components/newHomePage/utils/newHomePageUtils'
import { GroceryListItem } from '../components/groceryList/groceryList.types'
import { ingredientsObjectSelector } from '../app/commonSelector'

const filterGroceryItems = (items: GroceryListItem[], ingredients: any, userAllergens: string[], diet?: string) => {
    const allowedDiets = getDietArray(diet);
    if (allowedDiets.length === 0 && userAllergens.length === 0) {
        return items;
    }

    const itemsNew = items.filter((item: GroceryListItem) => {
        const mealDiet = item.Meal?.Recipes_E3?.diet || [];
        const mealIngredients = item.Meal?.Recipes_E3?.Recipe_Variations[0]?.ingredients || [];

        const isDietAllowed = allowedDiets.length === 0 || mealDiet.some((diet: string) => allowedDiets.includes(diet));
        if (!isDietAllowed)
            return false;
        const hasAllergenConflict = userAllergens.length > 0 && mealIngredients.some((ingredientId: string) => {
            const ingredientAllergens = ingredients[ingredientId]?.allergens || [];
            return ingredientAllergens?.length > 0 && ingredientAllergens.some((allergen: string) => userAllergens.includes(allergen));
        });
        return !hasAllergenConflict;
    });

    return itemsNew;
};



const useFetchGroceryList = () => {
    const user = useAppSelector(userSelector);
    const dispatch = useAppDispatch();
    const ingredients = useAppSelector(ingredientsObjectSelector);
    const userSettings = user?.userSettings;
    const userAllergens = userSettings?.allergens || [];
    const userDiet = userSettings?.diet;

    const getDatesFromNowCallback = useCallback(() => {
        return getDatesFromNow(6)
    }, []);
    const { lteCalculated, gteCalculated } = getDatesFromNowCallback();

    const { loading, refetch: refetchGroceryList } = useQuery(FETCH_USER_GROCERY_LIST, {
        skip: !user?.id || !ingredients || ingredients.length === 0,
        variables: {
            user_id: user?.id,
            lte: lteCalculated,
            gte: gteCalculated
        },
        onCompleted: ({ User_To_Shopping_List: shoppingLists }) => {
            const defaultShoppingList = shoppingLists?.[0];

            if (defaultShoppingList?.shopping_list_id) {
                dispatch(setGroceryListId(defaultShoppingList.shopping_list_id))
            }
            if (defaultShoppingList?.Shopping_List_Items?.length) {
                const filteredItems = filterGroceryItems(defaultShoppingList.Shopping_List_Items, ingredients, userAllergens, userDiet);
                const ingredientsList = filteredItems.map((item: any) => item.Ingredients_DB);
                dispatch(setGroceryListItemsAndIngredientsList({ groceryListItems: filteredItems, ingredientsList }));
            }
            else {
                dispatch(setGroceryListItems([]))
            }
        },

    })

    const refetch = async () => {
        const data = await refetchGroceryList();
        if (data?.data?.User_To_Shopping_List?.[0]?.shopping_list_id) {
            dispatch(setGroceryListId(data.data?.User_To_Shopping_List?.[0].shopping_list_id));
        }
        if (data?.data?.User_To_Shopping_List?.[0].Shopping_List_Items?.length) {
            const filteredItems = filterGroceryItems(data.data.User_To_Shopping_List[0].Shopping_List_Items, ingredients, userAllergens, userDiet);

            const ingredientsList = filteredItems.map((item: any) => item.Ingredients_DB);
            dispatch(setGroceryListItemsAndIngredientsList({ groceryListItems: filteredItems, ingredientsList }));

        } else {
            dispatch(setGroceryListItems([]));
        }
    };

    return { loading, refetch }

}

export default useFetchGroceryList;
